/**
 * Avalia se uma condição é verdadeira baseado nos dados do formulário
 * Suporta todos os operadores do Form Builder
 */
export function evaluateCondition(condition, formData, allFields = []) {
  const { field_id, operator, value } = condition;

  // Busca o valor do campo
  let fieldValue = undefined;
  let targetFieldName = field_id;

  // Se temos a lista de campos, procura pelo field_id ou _tempId
  if (allFields.length > 0) {
    const targetField = allFields.find(f =>
      String(f.id) === String(field_id) ||
      String(f._tempId) === String(field_id)
    );

    if (targetField) {
      targetFieldName = targetField.name;
      fieldValue = formData[targetField.name];
    }
  } else {
    // Fallback: tenta usar field_id diretamente como chave do formData
    fieldValue = formData[field_id];

    // Se ainda não achou, tenta encontrar uma chave que contenha o field_id
    if (fieldValue === undefined) {
      const fieldIdStr = String(field_id);
      const possibleKey = Object.keys(formData).find(key =>
        fieldIdStr.includes(key) || key.includes('has_companion')
      );
      if (possibleKey) {
        fieldValue = formData[possibleKey];
        targetFieldName = possibleKey;
      }
    }
  }

  switch (operator) {
    case '==':
    case 'equals':
      return fieldValue == value;

    case '!=':
    case 'not_equals':
      return fieldValue != value;

    case 'contains':
      return String(fieldValue || '').includes(value);

    case 'not_contains':
      return !String(fieldValue || '').includes(value);

    case 'is_empty':
      return !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined;

    case 'is_not_empty':
      return !!fieldValue && fieldValue !== '';

    case '>':
    case 'greater_than':
      return Number(fieldValue) > Number(value);

    case '<':
    case 'less_than':
      return Number(fieldValue) < Number(value);

    case '>=':
    case 'greater_than_or_equal':
      return Number(fieldValue) >= Number(value);

    case '<=':
    case 'less_than_or_equal':
      return Number(fieldValue) <= Number(value);

    case 'starts_with':
      return String(fieldValue || '').startsWith(value);

    case 'ends_with':
      return String(fieldValue || '').endsWith(value);

    case 'in': {
      // value deve ser array ou string separada por vírgula
      const values = Array.isArray(value) ? value : value.split(',').map(v => v.trim());
      return values.includes(String(fieldValue));
    }

    case 'not_in': {
      const notInValues = Array.isArray(value) ? value : value.split(',').map(v => v.trim());
      return !notInValues.includes(String(fieldValue));
    }

    default:
      console.warn(`Operador desconhecido: ${operator}`);
      return true; // Se operador desconhecido, mostra o campo
  }
}

/**
 * Avalia múltiplas condições com lógica AND/OR
 */
export function evaluateConditions(conditions, logic, formData, allFields = []) {
  if (!conditions || conditions.length === 0) return true;

  if (logic === 'or') {
    // Pelo menos uma condição verdadeira
    return conditions.some(cond => evaluateCondition(cond, formData, allFields));
  } else {
    // Todas as condições verdadeiras (AND)
    return conditions.every(cond => evaluateCondition(cond, formData, allFields));
  }
}

/**
 * Verifica se um campo deve ser exibido baseado em suas condições
 */
export function shouldShowField(field, formData, allFields = []) {
  if (!field) return false; // Campo null não é exibido
  if (!field.conditions || field.conditions.length === 0) {
    return true; // Sem condições = sempre exibir
  }

  // Processa condições novas (formato com rules)
  if (Array.isArray(field.conditions) && field.conditions[0]?.rules) {
    const condition = field.conditions[0];
    return evaluateConditions(condition.rules, condition.logic || 'and', formData, allFields);
  }

  // Formato antigo (compatibilidade)
  return evaluateConditions(field.conditions, field.condition_logic || 'and', formData, allFields);
}

/**
 * Verifica se um step deve ser exibido baseado em suas condições
 */
export function shouldShowStep(step, formData, allFields = []) {
  if (!step) return false; // Step null não é exibido
  if (!step.conditions || step.conditions.length === 0) {
    return true; // Sem condições = sempre exibir
  }

  // Processa condições novas (formato com rules)
  if (Array.isArray(step.conditions) && step.conditions[0]?.rules) {
    const condition = step.conditions[0];
    return evaluateConditions(condition.rules, condition.logic || 'and', formData, allFields);
  }

  // Formato antigo (compatibilidade)
  return evaluateConditions(step.conditions, step.condition_logic || 'and', formData, allFields);
}

/**
 * Filtra apenas os campos visíveis de um step baseado nas condições
 */
export function getVisibleFields(fields, formData, allFields = []) {
  return fields.filter(field => shouldShowField(field, formData, allFields));
}

/**
 * Retorna todos os campos obrigatórios visíveis de um step
 */
export function getRequiredVisibleFields(fields, formData, allFields = []) {
  return getVisibleFields(fields, formData, allFields).filter(field => field.required);
}
